/* eslint-disable no-restricted-globals */
/** @jsxImportSource theme-ui */
import axios from 'client';
import config from 'config';

import LogApi from 'api/LogApi';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { Message } from 'semantic-ui-react';
import { ThemeUIStyleObject } from 'theme-ui';

import theme from 'theme';
import { skeletonItemColor } from 'utils/cssHelpers';

import { Typography } from 'components/_common/Typography';
import BdButton from 'components/_controls/BdButton/BdButton';
import BdCheckbox from 'components/_controls/BdCheckbox/BdCheckbox';
import { LoadingSVG } from 'components/Loading/Loading';

const Styles: ThemeUIStyleObject = {
  color: theme.colors.stone90,
  margin: '16px 32px',
  '& .header-container': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '32px',
    '& svg': { marginRight: '16px' },
  },
  '& .subscription-form': { position: 'relative' },
  '& .loading-overlay': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: 'white',
    opacity: '0.75',
  },
  '& .loading-container': { position: 'absolute', top: '32px', left: 'calc(50% - 30px)' },
  '& .content-container': { padding: '0 24px' },
  '& .email-container': { marginBottom: '8px' },
  '& .email-disclaimer': { width: '500px', marginBottom: '32px' },
  '& .subscriptions-title': { marginBottom: '24px' },
  '& .checkbox': { marginBottom: '16px' },
  '& .preferences-button': { marginTop: '16px' },
  '& .name-skel': { ...skeletonItemColor, width: '180px', height: '16px' },
};

type FormState = {
  receivePeriodicNotableEventEmails: boolean;
  receiveRealtimeNotableEventEmails: boolean;
};

const PreferencesPage = () => {
  useEffect(() => {
    document.title = 'Email Preferences | BlueDot';
  }, []);

  const [saveState, setSaveState] = useState<'unsaved' | 'saved' | 'error' | 'no-user'>('unsaved');
  const [encryptedId, setEncryptedId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [initialFormState, setInitialFormState] = useState<FormState>({
    receivePeriodicNotableEventEmails: false,
    receiveRealtimeNotableEventEmails: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));

    if (!params.d) {
      setSaveState('no-user');
    } else {
      setIsLoading(true);

      axios
        .get(`${config.insightsApiBaseUrl}/api/userpreferences/${params.d}`)
        .then(({ data }) => {
          setInitialFormState({
            receiveRealtimeNotableEventEmails: data.receiveRealtimeNotableEventEmails,
            receivePeriodicNotableEventEmails: data.receivePeriodicNotableEventEmails,
          });
          setEncryptedId(params.d as string);
          setUserEmail(data.email);
          setIsLoading(false);
        })
        .catch(e => {
          setSaveState('no-user');
          setIsLoading(false);
        });
    }
  }, [location.search]);

  const handleSubmit = useCallback(
    (e: FormState) => {
      setIsSubmitting(true);
      setSaveState('unsaved');

      axios
        .post(`${config.insightsApiBaseUrl}/api/userpreferences`, {
          encryptedUserId: encryptedId,
          ...e,
        })
        .then(() => {
          setSaveState('saved');
          setIsSubmitting(false);
        })
        .catch(err => {
          LogApi.sendLog('error', `Message: ${err?.message}, Stack: ${err?.stack}`);
          console.error(err);
          setSaveState('error');
          setIsSubmitting(false);
        });
    },
    [encryptedId]
  );

  return (
    <div sx={Styles}>
      <div className="header-container">
        <svg
          width="36"
          height="45"
          viewBox="0 0 36 45"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.0002 22.4668C15.5025 22.4668 13.4805 24.4792 13.4805 26.9608C13.4805 29.4424 15.5044 31.4549 18.0002 31.4549C20.496 31.4549 22.5199 29.4424 22.5199 26.9608C22.5199 24.4792 20.496 22.4668 18.0002 22.4668Z"
            fill="#71C5E8"
          />
          <path
            d="M18 9.06281H9.03948C7.37563 9.06281 6.02568 7.72053 6.02568 6.06613V2.99285C6.02375 1.34037 4.67572 0 3.0138 0C1.34995 0 0 1.34228 0 2.99668V9.06281V21.5742V26.9606C0 36.8295 8.07468 44.8603 18 44.8603C27.9253 44.8603 36 36.8314 36 26.9606C36 17.0916 27.9253 9.06281 18 9.06281ZM18 38.8669C11.3966 38.8669 6.02568 33.5246 6.02568 26.9606V21.5742V15.0562H18C24.6015 15.0562 29.9724 20.3966 29.9724 26.9606C29.9724 33.5246 24.6015 38.8669 18 38.8669Z"
            fill="#042341"
          />
        </svg>
        <Typography variant="h3">Email Preferences</Typography>
      </div>
      <div className="content-container">
        {saveState === 'saved' && (
          <Message success content="Your preferences have been updated successfully!" />
        )}
        {saveState === 'error' && (
          <Message error content="There was an error processing your request, please try again" />
        )}
        {saveState === 'no-user' ? (
          <Message error>
            No associated user was found. Please try clicking the unsubscribe link in a newer email.
            If this keeps happening, contact{' '}
            <a href="mailto:support@bluedot.global">support@bluedot.global</a>.
          </Message>
        ) : (
          <>
            {isLoading ? (
              <div className="name-skel email-container" />
            ) : (
              <Typography className="email-container" variant="body-large-emphasized">
                {userEmail}
              </Typography>
            )}
            <Typography className="email-disclaimer" variant="body-large-regular">
              <Typography variant="body-large-semiemphasized" inline>
                Ignore this page if this is not your e-mail address.
              </Typography>{' '}
              It is likely that the associated e-mail was forwarded to you.
            </Typography>
            <Typography className="subscriptions-title" variant="body-large-emphasized">
              Your Subscriptions
            </Typography>
            {isLoading ? (
              <>
                <div className="loading-overlay" />
                <LoadingSVG />
              </>
            ) : (
              <Formik initialValues={initialFormState} onSubmit={handleSubmit}>
                {({ values, setFieldValue }) => (
                  <Form className="subscription-form">
                    <BdCheckbox
                      label="Bi-weekly brief - A summary of all notable event activity ongoing around the world, in one email."
                      value={values.receivePeriodicNotableEventEmails ? 'checked' : 'none'}
                      onChange={() =>
                        setFieldValue(
                          'receivePeriodicNotableEventEmails',
                          !values.receivePeriodicNotableEventEmails
                        )
                      }
                    />
                    <BdCheckbox
                      label="Real-time event alerts - Singular updates on new notable events or follow-ups on existing notable events, delivered as they are discovered."
                      value={values.receiveRealtimeNotableEventEmails ? 'checked' : 'none'}
                      onChange={() =>
                        setFieldValue(
                          'receiveRealtimeNotableEventEmails',
                          !values.receiveRealtimeNotableEventEmails
                        )
                      }
                    />
                    <BdButton
                      className="preferences-button"
                      type="primary"
                      buttonType="submit"
                      text="Save"
                      disabled={isSubmitting}
                    />
                    {isSubmitting && (
                      <>
                        <div className="loading-overlay" />
                        <LoadingSVG />
                      </>
                    )}
                  </Form>
                )}
              </Formik>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PreferencesPage;
